'use client';

import { c as _c } from "react/compiler-runtime";
import { useMandatoryContext } from '@repo/hooks';
import { Button, MultiStepDialogProvider, Popover, PopoverAnchor, PopoverContent, type StepId } from '@repo/ui';
import { Close } from '@repo/ui/icons';
import { createContext, type ComponentProps, type ComponentType, type Key, type ReactNode, type RefObject, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useActionFlow } from '../ActionFlowProvider';
import { isActionFlowFinalizing } from '../utils/isActionFlowFinalizing';
import { isActionFlowMayStillLand } from '../utils/isActionFlowMayStillLand';
type DeferredAction = () => void;
const noop = () => {};
interface ExitConfirmationCopy {
  title: string;
  body: string;
  confirmLabel: string;
}

// Once a submission may still land, closing can no longer cancel it, so the copy says "close".
function getExitConfirmationCopy({
  isAwaitingSafeProposal,
  mayStillLand
}: {
  isAwaitingSafeProposal: boolean;
  mayStillLand: boolean;
}): ExitConfirmationCopy {
  if (isAwaitingSafeProposal) return {
    title: 'Close Dialog',
    body: 'Your transaction is in your Safe queue and may still execute. Check your Safe queue before starting over to avoid submitting it twice.',
    confirmLabel: 'Close anyway'
  };
  if (mayStillLand) return {
    title: 'Close Dialog',
    body: 'Your last transaction may still go through. Check your Safe queue or wallet activity before starting over to avoid submitting it twice.',
    confirmLabel: 'Close anyway'
  };
  return {
    title: 'Cancel Action',
    body: 'Are you sure you want to close this modal? It will interrupt the current flow and you will need to start over.',
    confirmLabel: 'Exit anyway'
  };
}
interface ActionFlowDialogCloseButtonProps {
  onClick: () => void;
  buttonRef: RefObject<HTMLButtonElement | null>;
}
function ActionFlowDialogCloseButton(t0) {
  "use memo";

  const $ = _c(4);
  const {
    onClick,
    buttonRef
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Close />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== buttonRef || $[2] !== onClick) {
    t2 = <div className="pointer-events-none sticky top-0 z-20 order-first h-0" data-slot="action-flow-dialog-close"><PopoverAnchor asChild={true}><Button variant="ghost" size="icon" aria-label="Close" data-testid="action-flow-dialog-close" className="top-lg pointer-events-auto absolute right-0 -translate-y-[4px]" ref={buttonRef} onClick={onClick}>{t1}</Button></PopoverAnchor></div>;
    $[1] = buttonRef;
    $[2] = onClick;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
interface ActionFlowDialogContextValue {
  requestClose: (deferredAction?: DeferredAction) => boolean;
  exit: (deferredAction?: DeferredAction) => void;
}
const ActionFlowDialogContext = createContext<ActionFlowDialogContextValue | null>(null);

/**
 * Close controls for content inside an `ActionFlowDialog`; throws outside one.
 *
 * - `exit(action?)` closes without asking. Once the content has detached, the dialog's `onExit`
 *   runs, then `action`. A second call during the exit animation is ignored.
 * - `requestClose(action?)` asks for confirmation while a flow is active and returns `true`;
 *   confirming calls `exit(action)`. Otherwise it closes, returns `false` and does not run
 *   `action`, so the caller handles the follow-up itself (e.g. a link navigating).
 */
export const useActionFlowDialog = () => {
  return useMandatoryContext(ActionFlowDialogContext, "ActionFlowDialog");
};
type ActionFlowDialogProviderProps = ComponentProps<typeof MultiStepDialogProvider> & {
  /** Changes when the dialog swaps to the result screen without changing step. */
  stepKey?: Key;
};
interface ActionFlowDialogProps {
  open: boolean;
  onExit: () => void;
  /**
   * Runs when the user confirms closing ("Exit anyway", or "Close anyway" once a submission may still land) while flow steps remain, before the
   * dialog closes. Not called once the last step is finalizing (e.g. while `onSuccess` runs). Can
   * still fire while the receipt for a confirmed final batch with an `after` callback is fetched.
   */
  onCancelActiveFlow?: () => void;
  children: ReactNode;
  initialStepId?: StepId;
  className?: string;
  dataTestId?: string;
  /** `id` of the dialog content, for a trigger's `aria-controls`. */
  contentId?: string;
  /** Renders the dialog shell. Defaults to `@repo/ui`'s `MultiStepDialogProvider`; apps pass their own to animate step changes. */
  DialogProvider?: ComponentType<ActionFlowDialogProviderProps>;
  fullScreenMobile?: boolean;
}

/**
 * Dialog around an action flow that guards against closing it mid-flow. While a transaction is
 * awaiting signature or confirmation, or a flow callback is running, Escape and backdrop clicks
 * are ignored and the close button asks for confirmation. While a Safe proposal is queued, or the
 * last submission may still land, the confirmation tells the user to check before starting over.
 *
 * Confirming "Exit anyway" aborts the running execution at once, so no further wallet request
 * starts during the exit animation. Closing keeps `children` rendered through that animation. Once
 * the content has detached it resets the action flow, then calls `onExit`, so `onExit` may tear
 * down the state `children` render from. Setting `open` to false from the parent closes
 * the dialog without resetting the flow or calling `onExit`.
 */
export function ActionFlowDialog(t0) {
  "use memo";

  const $ = _c(88);
  const {
    open,
    onExit,
    onCancelActiveFlow,
    children,
    initialStepId,
    className,
    dataTestId,
    contentId,
    DialogProvider: t1,
    fullScreenMobile
  } = t0;
  const DialogProvider = t1 === undefined ? MultiStepDialogProvider : t1;
  const {
    state,
    isCallbackInFlight,
    stepLabels,
    abort,
    reset,
    reportRenderError
  } = useActionFlow();
  const [isConfirmingExit, setIsConfirmingExit] = useState(false);
  const pendingActionRef = useRef(undefined);
  const closeButtonRef = useRef(null);
  const [pendingExit, setPendingExit] = useState(null);
  let t2;
  if ($[0] !== state) {
    t2 = isActionFlowFinalizing(state);
    $[0] = state;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isFinalizing = t2;
  const isFlowActive = state.flowState === "execution" && !isFinalizing || isCallbackInFlight;
  const isTerminal = state.flowState === "success" || state.flowState === "reverted";
  const isAwaitingSafeProposal = state.flowState === "execution" && state.pendingSafeTxHash !== undefined;
  let mayStillLand;
  let needsExitConfirmation;
  let t3;
  if ($[2] !== isAwaitingSafeProposal || $[3] !== isFlowActive || $[4] !== state) {
    mayStillLand = isActionFlowMayStillLand(state);
    needsExitConfirmation = isFlowActive || mayStillLand;
    t3 = getExitConfirmationCopy({
      isAwaitingSafeProposal,
      mayStillLand
    });
    $[2] = isAwaitingSafeProposal;
    $[3] = isFlowActive;
    $[4] = state;
    $[5] = mayStillLand;
    $[6] = needsExitConfirmation;
    $[7] = t3;
  } else {
    mayStillLand = $[5];
    needsExitConfirmation = $[6];
    t3 = $[7];
  }
  const exitCopy = t3;
  let t4;
  if ($[8] !== pendingExit) {
    t4 = deferredAction => {
      if (pendingExit) {
        return;
      }
      setPendingExit(() => deferredAction ?? noop);
    };
    $[8] = pendingExit;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const exit = t4;
  let t5;
  if ($[10] !== exit || $[11] !== isConfirmingExit || $[12] !== needsExitConfirmation) {
    t5 = deferredAction_0 => {
      if (needsExitConfirmation) {
        pendingActionRef.current = deferredAction_0;
        if (isConfirmingExit) {
          return true;
        }
        setIsConfirmingExit(true);
        return true;
      }
      exit();
      return false;
    };
    $[10] = exit;
    $[11] = isConfirmingExit;
    $[12] = needsExitConfirmation;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const requestClose = t5;
  let t6;
  if ($[14] !== abort || $[15] !== exit || $[16] !== isFinalizing || $[17] !== onCancelActiveFlow || $[18] !== pendingExit || $[19] !== state.flowState || $[20] !== state.stepIndex || $[21] !== stepLabels) {
    t6 = () => {
      setIsConfirmingExit(false);
      const action = pendingActionRef.current;
      pendingActionRef.current = undefined;
      const hasStepsLeft = state.flowState === "execution" && (!isFinalizing || state.stepIndex < stepLabels.length - 1);
      if (hasStepsLeft && !pendingExit) {
        abort();
        onCancelActiveFlow?.();
      }
      exit(action);
    };
    $[14] = abort;
    $[15] = exit;
    $[16] = isFinalizing;
    $[17] = onCancelActiveFlow;
    $[18] = pendingExit;
    $[19] = state.flowState;
    $[20] = state.stepIndex;
    $[21] = stepLabels;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const confirmExit = t6;
  let t7;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsConfirmingExit(false);
      pendingActionRef.current = undefined;
    };
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const cancelExit = t7;
  let t8;
  if ($[24] !== exit || $[25] !== isFlowActive || $[26] !== mayStillLand || $[27] !== requestClose) {
    t8 = nextOpen => {
      if (nextOpen || isFlowActive) {
        return;
      }
      if (mayStillLand) {
        requestClose();
      } else {
        exit();
      }
    };
    $[24] = exit;
    $[25] = isFlowActive;
    $[26] = mayStillLand;
    $[27] = requestClose;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const handleOpenChange = t8;
  let t9;
  if ($[29] !== onExit || $[30] !== pendingExit || $[31] !== reset) {
    t9 = () => {
      if (!pendingExit) {
        return;
      }
      setPendingExit(null);
      reset();
      onExit();
      pendingExit();
    };
    $[29] = onExit;
    $[30] = pendingExit;
    $[31] = reset;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const handleContentDetach = t9;
  const [retainedChildren, setRetainedChildren] = useState(children);
  if (isConfirmingExit && (!open || !needsExitConfirmation)) {
    setIsConfirmingExit(false);
  }
  if (open && retainedChildren !== children) {
    setRetainedChildren(children);
  }
  const renderedChildren = open ? children : retainedChildren;
  let t10;
  if ($[33] !== exit || $[34] !== requestClose) {
    t10 = {
      requestClose,
      exit
    };
    $[33] = exit;
    $[34] = requestClose;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const t11 = open && pendingExit === null;
  const t12 = isTerminal ? "result" : "flow";
  let t13;
  if ($[36] !== isTerminal || $[37] !== requestClose) {
    t13 = !isTerminal && <ActionFlowDialogCloseButton buttonRef={closeButtonRef} onClick={() => requestClose()} />;
    $[36] = isTerminal;
    $[37] = requestClose;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== isTerminal || $[40] !== requestClose) {
    t14 = isTerminal && <ActionFlowDialogCloseButton buttonRef={closeButtonRef} onClick={() => requestClose()} />;
    $[39] = isTerminal;
    $[40] = requestClose;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div role="alert" className="text-destructive-foreground p-4">This screen couldn't be displayed. Any transaction you already sent is not affected. Close and reopen this dialog.</div>;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t14) {
    t16 = <>{t14}{t15}</>;
    $[43] = t14;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== renderedChildren || $[46] !== reportRenderError || $[47] !== t16) {
    t17 = <ErrorBoundary onError={reportRenderError} fallback={t16}>{renderedChildren}</ErrorBoundary>;
    $[45] = renderedChildren;
    $[46] = reportRenderError;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== DialogProvider || $[50] !== className || $[51] !== contentId || $[52] !== dataTestId || $[53] !== fullScreenMobile || $[54] !== handleContentDetach || $[55] !== handleOpenChange || $[56] !== initialStepId || $[57] !== t11 || $[58] !== t12 || $[59] !== t13 || $[60] !== t17) {
    t18 = <DialogProvider isOpen={t11} initialStepId={initialStepId} stepKey={t12} className={className} dataTestId={dataTestId} contentId={contentId} fullScreenMobile={fullScreenMobile} closeOnEscape={true} showCloseButton={false} onOpenChange={handleOpenChange} onContentDetach={handleContentDetach}>{t13}{t17}</DialogProvider>;
    $[49] = DialogProvider;
    $[50] = className;
    $[51] = contentId;
    $[52] = dataTestId;
    $[53] = fullScreenMobile;
    $[54] = handleContentDetach;
    $[55] = handleOpenChange;
    $[56] = initialStepId;
    $[57] = t11;
    $[58] = t12;
    $[59] = t13;
    $[60] = t17;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  let t19;
  if ($[62] !== t10 || $[63] !== t18) {
    t19 = <ActionFlowDialogContext.Provider value={t10}>{t18}</ActionFlowDialogContext.Provider>;
    $[62] = t10;
    $[63] = t18;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  let t20;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = event => {
      event.preventDefault();
      closeButtonRef.current?.focus();
    };
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  let t21;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = event_0 => {
      event_0.preventDefault();
      event_0.stopPropagation();
      cancelExit();
    };
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== exitCopy.title) {
    t22 = <strong className="text-md">{exitCopy.title}</strong>;
    $[67] = exitCopy.title;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  let t23;
  if ($[69] !== exitCopy.body) {
    t23 = <span className="text-2xs text-muted-foreground">{exitCopy.body}</span>;
    $[69] = exitCopy.body;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  let t24;
  if ($[71] !== t22 || $[72] !== t23) {
    t24 = <div className="gap-xs flex flex-col">{t22}{t23}</div>;
    $[71] = t22;
    $[72] = t23;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== confirmExit || $[75] !== exitCopy.confirmLabel) {
    t25 = <Button fullWidth={true} variant="destructive" onClick={confirmExit}>{exitCopy.confirmLabel}</Button>;
    $[74] = confirmExit;
    $[75] = exitCopy.confirmLabel;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Button fullWidth={true} variant="ghost" onClick={cancelExit}>Keep going</Button>;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  let t27;
  if ($[78] !== t24 || $[79] !== t25) {
    t27 = <div className="gap-sm flex flex-col">{t24}{t25}{t26}</div>;
    $[78] = t24;
    $[79] = t25;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  let t28;
  if ($[81] !== exitCopy.title || $[82] !== t27) {
    t28 = <PopoverContent aria-label={exitCopy.title} align="end" className="w-72" side="top" sideOffset={8} onCloseAutoFocus={t20} onPointerDownOutside={cancelExit} onFocusOutside={cancelExit} onEscapeKeyDown={t21}>{t27}</PopoverContent>;
    $[81] = exitCopy.title;
    $[82] = t27;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  let t29;
  if ($[84] !== isConfirmingExit || $[85] !== t19 || $[86] !== t28) {
    t29 = <Popover open={isConfirmingExit} modal={false}>{t19}{t28}</Popover>;
    $[84] = isConfirmingExit;
    $[85] = t19;
    $[86] = t28;
    $[87] = t29;
  } else {
    t29 = $[87];
  }
  return t29;
}